import { observer } from 'mobx-react';
import { WALLETS_TRANSACTIONS_COLUMN } from '~/view/screens/WalletTransactionLogScreen/screens/WTLogScreen/constants/walletTransactionRow';
import { Icons } from 'src/assets/fonts/Icons/icons';
import { SORTING_DIRECTION } from 'src/constants/main';
import { localeStore } from 'src/mobx/localesStore';
import { formatImagePath } from 'src/utils/formatter';
import { emptyArray } from 'src/utils/helpers';
import ColumnTitle from 'src/view/components/ColumnTitle/ColumnTitle';
import Pagination from 'src/view/components/Controls/PaginationOld/PaginationOld';
import { PaginationType } from 'src/view/components/Controls/PaginationOld/types';
import ScrollableBlock from 'src/view/containers/ScrollableBlock/ScrollableBlock';
import {
  compareFiltersWithDefault,
  getValidWTLogColumnsWidth,
} from 'src/view/screens/WalletTransactionLogScreen/screens/WTLogScreen/helpers/walletTransaction';
import walletTransactionStore from 'src/view/screens/WalletTransactionLogScreen/screens/WTLogScreen/store/walletTransactionStore';
import WTLogListRow from 'src/view/screens/WalletTransactionLogScreen/screens/WTLogScreen/components/WTLogListRow/WTLogListRow';
import './style.scss';

const WTLogList = (): JSX.Element => {
  // --------------- Handlers ---------------
  const onChangeSorting = (sortBy: WALLETS_TRANSACTIONS_COLUMN | undefined, sortDir: SORTING_DIRECTION | undefined) => {
    walletTransactionStore.setFilters({ sortBy, sortDir });
  };

  const { pagination, filters } = walletTransactionStore;

  const columnsWidth = getValidWTLogColumnsWidth();

  const isListLoading = (pagination.loading || pagination.refreshing) && !pagination.total;
  const emptyListTitle =
    walletTransactionStore.isEmptyWalletTransactionList && compareFiltersWithDefault(walletTransactionStore.filters)
      ? 'no_wallet_transaction_yet'
      : 'no_wallet_transaction_found';

  const getColumnTitle = (name: WALLETS_TRANSACTIONS_COLUMN, title: string, width: number) => {
    return (
      <ColumnTitle<WALLETS_TRANSACTIONS_COLUMN>
        columnName={name}
        title={title}
        currentField={filters.sortBy}
        currentDir={filters.sortDir}
        setFilters={onChangeSorting}
        className={`w-${width}`}
      />
    );
  };

  return (
    <div className="d-flex flex-column flex-1 mt-4">
      {!pagination.data.length && !pagination.loading ? (
        <div className="d-flex flex-column justify-content-center align-items-center mt-6">
          <img src={formatImagePath(`${Icons.BranchesTree}.svg`)} alt={emptyListTitle} className="image__no-found mb-6" />
          <p className="family-medium text-size-24 color-gray2">{localeStore.t(emptyListTitle)}</p>
        </div>
      ) : (
        <ScrollableBlock
          showScrollbar
          scrollClassName="d-flex flex-column full-height pr-3"
          className="overflow-y-scroll mb-3 wt-log"
          direction="vertical"
        >
          <div className="d-flex flex-row pb-4 wt-log-headers">
            {getColumnTitle(WALLETS_TRANSACTIONS_COLUMN.DEPOSIT_DATE, 'deposit_date', columnsWidth.depositDate)}
            {getColumnTitle(WALLETS_TRANSACTIONS_COLUMN.AUTHOR, 'author', columnsWidth.author_name)}
            {getColumnTitle(WALLETS_TRANSACTIONS_COLUMN.OBJECT, 'object', columnsWidth.object)}
            {getColumnTitle(WALLETS_TRANSACTIONS_COLUMN.TRANSACTION_AMOUNT, 'transaction_amount', columnsWidth.transaction_amount)}
            {getColumnTitle(WALLETS_TRANSACTIONS_COLUMN.BALANCE_TYPE, 'balance_type', columnsWidth.balance_type)}
            {getColumnTitle(WALLETS_TRANSACTIONS_COLUMN.TRANSACTION_TYPE, 'transaction_type', columnsWidth.transaction_type)}
            {getColumnTitle(WALLETS_TRANSACTIONS_COLUMN.REASON, 'reason', columnsWidth.reason)}
            {getColumnTitle(WALLETS_TRANSACTIONS_COLUMN.ORDER_NUMBER, 'order_number_1', columnsWidth.order_number)}
            {getColumnTitle(WALLETS_TRANSACTIONS_COLUMN.EXPIRATION_DATE, 'expiration_date', columnsWidth.expiration_date)}
            {getColumnTitle(WALLETS_TRANSACTIONS_COLUMN.WALLET_ID, 'wallet_id', columnsWidth.wallet_id)}
          </div>

          {isListLoading
            ? emptyArray().map((_, index) => <WTLogListRow key={index} columnsWidth={columnsWidth} />)
            : pagination.data.map((walletTransaction) => (
                <WTLogListRow key={walletTransaction.transaction_id} walletTransaction={walletTransaction} columnsWidth={columnsWidth} />
              ))}
        </ScrollableBlock>
      )}
      <Pagination
        pagination={pagination}
        showPaginationInfo={true}
        showMorePageButtons={true}
        type={PaginationType.ARROW}
        onChange={walletTransactionStore.fetchWalletTransactionList}
      />
    </div>
  );
};

export default observer(WTLogList);
